import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { FileText, History, Plus, Wrench, Bug, Trash2, Shield } from 'lucide-react';
import { CHANGELOG_ENTRIES, type ChangeType } from '@/lib/changelog';

const CHANGE_TYPE_CONFIG: Record<ChangeType, { label: string; icon: React.ElementType; className: string }> = {
  added: { label: 'Tillagt', icon: Plus, className: 'bg-green-500/10 text-green-600 dark:text-green-400' },
  changed: { label: 'Ändrat', icon: Wrench, className: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  fixed: { label: 'Fixat', icon: Bug, className: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400' },
  removed: { label: 'Borttaget', icon: Trash2, className: 'bg-red-500/10 text-red-600 dark:text-red-400' },
  security: { label: 'Säkerhet', icon: Shield, className: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
};

const PROJECT_STRUCTURE = {
  overview: 'Korsordslexikon är en svensk korsordssökmotor byggd med React, TypeScript, Tailwind CSS och Supabase.',
  pages: [
    { name: 'Index.tsx', description: 'Startsida med sökfunktionalitet för ord och ledtrådar' },
    { name: 'Forum.tsx', description: 'Diskussionsforum för användare' },
    { name: 'Auth.tsx', description: 'Inloggning och registrering' },
    { name: 'Account.tsx', description: 'Användarens kontosida med profilinställningar' },
    { name: 'Admin.tsx', description: 'Administratörspanel för hantering av ord, användare och innehåll' },
    { name: 'Statistics.tsx', description: 'Statistik över databasen och användning' },
  ],
  componentCategories: [
    {
      name: 'Sökkomponenter',
      components: ['WordSearch', 'AnagramSearch', 'CombinedSearch', 'SearchInput', 'SearchResults', 'SearchTabs']
    },
    {
      name: 'Forumkomponenter',
      components: ['ForumContent', 'ForumCategoryList', 'ForumTopicList', 'ForumTopicDetail', 'ForumReplyForm', 'ForumNewTopicForm', 'ForumAvatar', 'ForumAdminPanel']
    },
    {
      name: 'Admin-komponenter',
      components: ['WordSuggestionsAdmin', 'ClueSuggestionsAdmin', 'UserManagementAdmin', 'WordImportAdmin', 'ClueImportAdmin', 'ActivityLogAdmin', 'BackupAdmin', 'DatabaseManagementAdmin', 'AvatarReviewAdmin', 'SearchHistoryAdmin', 'DocsAdmin']
    }
  ],
  hooks: [
    { name: 'useAuth', description: 'Hanterar autentisering och användarroller' },
    { name: 'useWordSearch', description: 'Sökning efter ord baserat på mönster' },
    { name: 'useForum', description: 'CRUD-operationer för forum (topics, replies, kategorier)' },
    { name: 'useWordSuggestions', description: 'Hantering av ordförslag från användare' },
    { name: 'useClueSuggestions', description: 'Hantering av ledtrådsförslag från användare' },
    { name: 'useTheme', description: 'Temahantaring (ljust/mörkt läge)' },
    { name: 'useSpamProtection', description: 'Skydd mot spam-inlägg' },
  ],
  tables: [
    { name: 'words', description: 'Alla ord i databasen med språkbitmask' },
    { name: 'clues', description: 'Ledtrådar kopplade till ord' },
    { name: 'profiles', description: 'Användarprofiler med visningsnamn och avatar' },
    { name: 'user_roles', description: 'Roller (admin, moderator, user)' },
    { name: 'forum_categories', description: 'Forumkategorier' },
    { name: 'forum_topics', description: 'Foruminlägg/trådar' },
    { name: 'forum_replies', description: 'Svar på foruminlägg' },
    { name: 'word_suggestions', description: 'Ordförslag från användare' },
    { name: 'clue_suggestions', description: 'Ledtrådsförslag från användare' },
    { name: 'activity_logs', description: 'Loggning av administrativa händelser' },
    { name: 'search_logs', description: 'Loggning av sökningar' },
    { name: 'page_visits', description: 'Besöksstatistik' },
  ]
};

export function DocsAdmin() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Projektdokumentation
        </CardTitle>
        <CardDescription>
          Dokumentation och ändringslogg för projektet
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="changelog" className="space-y-4">
          <TabsList>
            <TabsTrigger value="changelog" className="gap-2">
              <History className="h-4 w-4" />
              Changelog
            </TabsTrigger>
            <TabsTrigger value="structure" className="gap-2">
              <FileText className="h-4 w-4" />
              Projektstruktur
            </TabsTrigger>
          </TabsList>

          <TabsContent value="changelog" className="space-y-3">
            <p className="text-sm text-muted-foreground mb-4">
              Aktuell version: <Badge variant="outline" className="ml-1 font-mono">{CHANGELOG_ENTRIES[0]?.version}</Badge>
            </p>
            {CHANGELOG_ENTRIES.map((entry, idx) => {
              const config = CHANGE_TYPE_CONFIG[entry.type];
              const Icon = config.icon;
              return (
                <div 
                  key={`${entry.version}-${idx}`} 
                  className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                >
                  <div className={`p-1.5 rounded ${config.className}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="secondary" className="font-mono text-xs">
                        v{entry.version}
                      </Badge>
                      <Badge variant="outline" className={`text-xs ${config.className}`}>
                        {config.label}
                      </Badge>
                      <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">
                        {entry.file}
                      </code>
                      <span className="text-xs text-muted-foreground ml-auto">
                        {entry.date}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {entry.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="structure" className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Översikt</h3>
              <p className="text-muted-foreground">{PROJECT_STRUCTURE.overview}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Sidor</h3>
              <div className="grid gap-2">
                {PROJECT_STRUCTURE.pages.map((page) => (
                  <div key={page.name} className="flex gap-2 text-sm">
                    <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono shrink-0">
                      {page.name}
                    </code>
                    <span className="text-muted-foreground">{page.description}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Komponenter</h3>
              <div className="space-y-4">
                {PROJECT_STRUCTURE.componentCategories.map((cat) => (
                  <div key={cat.name}>
                    <h4 className="font-medium text-primary mb-2">{cat.name}</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {cat.components.map((comp) => (
                        <code key={comp} className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">
                          {comp}
                        </code>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Hooks</h3>
              <div className="grid gap-2">
                {PROJECT_STRUCTURE.hooks.map((hook) => (
                  <div key={hook.name} className="flex gap-2 text-sm">
                    <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono shrink-0">
                      {hook.name}
                    </code>
                    <span className="text-muted-foreground">{hook.description}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Databastabeller</h3>
              <div className="grid gap-2">
                {PROJECT_STRUCTURE.tables.map((table) => (
                  <div key={table.name} className="flex gap-2 text-sm">
                    <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono shrink-0">
                      {table.name}
                    </code>
                    <span className="text-muted-foreground">{table.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
